import { Injectable } from '@angular/core';
import { JSONSchema7 } from 'json-schema';

import { FieldType } from '@azavista/advanced-search/lib/field-types';
import { IField, IRatingFieldOptions } from './interfaces';
import { UtilsService } from '../shared/utils.service';
import { FieldComponentType, RatingFieldOptionVisualStyle } from './enums';
import { dataAttributesFalse, dataAttributesForInactiveElement } from './fake-input-field-attributes';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root'
})
export class InputFieldCreatorService {
    private validateFnName = 'azavista.validateContactField';

    constructor(private utilsSvc: UtilsService, private translateSvc: TranslateService,) {
    }

    getInputElementHtml(field: IField, inputType: FieldType): string {

        const labelText = `
        <label class="aza-text" ${dataAttributesFalse(['editable'])} data-azavista-translatable>
                ${this.utilsSvc.escapeHtml(field.label)} <span></span></label>
        `;
        let inputText: string;
        if (field.component === FieldComponentType.textarea) {
            inputText = `
        <textarea id="${field.id}-input-field" field-id="${field.id}" rows="4" ${dataAttributesFalse(['editable'])}
            ${dataAttributesForInactiveElement()}
            oninput="${this.validateFnName}('${field.id}', this, '${field.id}-field-validation-errors')">
        </textarea>
        `;
        } else if (inputType === 'datetime' || inputType === 'travel-date') {
            inputText = `
                <div class="date-time-container" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()} style="display:flex">
                  <input class="date-input" date-input field-id="${field.id}" id="${field.id}-input-field" type="date"
                  ${dataAttributesFalse(['editable'])}
                  oninput="${this.validateFnName}('${field.id}', this, '${field.id}-field-validation-errors')" ${dataAttributesForInactiveElement()}/>
                  <span style="width:1em; opacity:0"></span>
                  <input class="time-input" time-input field-id="${field.id}" id="${field.id}-input-field" type="time"
                  ${dataAttributesFalse(['editable'])}
                  oninput="${this.validateFnName}('${field.id}', this, '${field.id}-field-validation-errors')" ${dataAttributesForInactiveElement()}/>
                </div>
        `;
        } else if (inputType === 'opinion-scale') {
            inputText = this.getOpinionScaleElementHtml(field);
        } else if (inputType === 'rating') {
            inputText = this.getRatingElementHtml(field);
        }
        else {
            inputText = `
        <input id="${field.id}-input-field" field-id="${field.id}" type="${inputType}" ${dataAttributesFalse(['editable'])}
            ${dataAttributesForInactiveElement()}
            oninput="${this.validateFnName}('${field.id}', this, '${field.id}-field-validation-errors')" />
        `;
        }
        const inputFieldText = `${labelText}${inputText}`;
        return inputFieldText;
    }

    getRatingElementHtml(field: IField): string {
        const max = +field.schema.maximum;
        let optionsText = '';
        const defaultValue = +field.schema.default || 1;
        const fieldOptions = field.options as IRatingFieldOptions;
        for (let i = 1; i <= max; i++) {
            const isIncluded = i <= defaultValue;
            const nonIncludedVisibleClass = isIncluded ? 'aza-rating-not-visible-item' : 'aza-rating-visible-item';
            const includedVisibleClass = isIncluded ? 'aza-rating-visible-item' : 'aza-rating-not-visible-item';
            const ratingTypeClass = 'aza-rating-type-' + fieldOptions.visualStyle;
            optionsText +=
                `
<span aza-rating-field-value="${i}" aza-rating-field-not-included-item
  class="aza-rating-field-value aza-rating-not-included-value ${nonIncludedVisibleClass} ${ratingTypeClass}" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
${this.getRatingSvg(field, false)}
</span>
<span aza-rating-field-value="${i}" aza-rating-field-included-item
  class="aza-rating-field-value aza-rating-included-value ${includedVisibleClass} ${ratingTypeClass}" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
${this.getRatingSvg(field, true)}
</span>
`;
        }
        const text =
            `
<div aza-rating-field class="rating-field-container" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
  <input aza-rating-input-field id="${field.id}-input-field" field-id="${field.id}" type="number" style="display: none;" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()} />
  <div aza-rating-field-values-container class="rating-field-values-container" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
${optionsText}
  </div>
</div>
`;
        return text;
    }

    getRatingSvg(field: IField, filled: boolean): string {
        const opts = field.options as IRatingFieldOptions;
        const filledSvgClass = 'aza-rating-filled-svg';
        const notFilledSvgClass = 'aza-rating-not-filled-svg';
        let svgText: string;
        if (opts.visualStyle === RatingFieldOptionVisualStyle.hearts) {
            if (filled) {
                svgText = `
                <svg xmlns="http://www.w3.org/2000/svg" class="${filledSvgClass}" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
                  <path d="M0 0h24v24H0V0z" fill="none" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                </svg>
                `;
            } else {
                svgText = `
                <svg xmlns="http://www.w3.org/2000/svg" class="${notFilledSvgClass}" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
                  <path d="M0 0h24v24H0V0z" fill="none" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                  <path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                </svg>
                `;
            }
        } else if (opts.visualStyle === RatingFieldOptionVisualStyle.stars) {
            if (filled) {
                svgText = `
                <svg xmlns="http://www.w3.org/2000/svg" class="${filledSvgClass}" enable-background="new 0 0 24 24" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
                  <g ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
                    <rect fill="none" height="24" width="24" x="0" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                    <polygon points="14.43,10 12,2 9.57,10 2,10 8.18,14.41 5.83,22 12,17.31 18.18,22 15.83,14.41 22,10" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                  </g>
                </svg>
                `;
            } else {
                svgText = `
                <svg xmlns="http://www.w3.org/2000/svg" class="${notFilledSvgClass}" enable-background="new 0 0 24 24" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
                  <g ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
                    <rect fill="none" height="24" width="24" x="0" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                    <path d="M12,8.89L12.94,12h2.82l-2.27,1.62l0.93,3.01L12,14.79l-2.42,1.84l0.93-3.01L8.24,12h2.82L12,8.89 M12,2l-2.42,8H2 l6.17,4.41L5.83,22L12,17.31L18.18,22l-2.35-7.59L22,10h-7.58L12,2L12,2z" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                  </g>
                </svg>
                `;
            }
        } else if (opts.visualStyle === RatingFieldOptionVisualStyle.thumbs) {
            if (filled) {
                svgText = `
                <svg xmlns="http://www.w3.org/2000/svg" class="${filledSvgClass}" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
                  <path d="M0 0h24v24H0V0z" fill="none" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                  <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                </svg>`;
            } else {
                svgText = `
                <svg xmlns="http://www.w3.org/2000/svg" class="${notFilledSvgClass}" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
                  <path d="M0 0h24v24H0V0zm0 0h24v24H0V0z" fill="none" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                  <path d="M9 21h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.58 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2zM9 9l4.34-4.34L12 10h9v2l-3 7H9V9zM1 9h4v12H1z" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
                </svg>`;
            }
        }
        return svgText;
    }

    getOpinionScaleElementHtml(field: IField): string {
        const max = +field.schema.maximum;
        let optionsText = '';
        const defaultValue = +field.schema.default;
        for (let i = 1; i <= max; i++) {
            const selectedClass = i === defaultValue ? 'aza-opinion-scale-selected-value' : '';
            optionsText += `<span aza-opinion-scale-field-value="${i}" class="aza-opinion-scale-field-value ${selectedClass}" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>${i}</span>`;
        }
        const text =
            `
<div aza-opinion-scale-field class="opinion-scale-field-container" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
  <input aza-opinion-scale-input-field id="${field.id}-input-field" field-id="${field.id}" type="number" oninput="azavista.validateField('${field.id}', this, '{${field.id}-field-validation-errors')" style="display: none;" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()} />
  <div aza-opinion-scale-field-values-container class="opinion-scale-field-values-container" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
<span class="opinion-scale-field-minimum-meaning" data-azavista-translatable ${dataAttributesFalse(['editable'])} ${dataAttributesForInactiveElement()}>Minimum meaning</span>
${optionsText}
<span class="opinion-scale-field-maximum-meaning" data-azavista-translatable ${dataAttributesFalse(['editable'])} ${dataAttributesForInactiveElement()}>Maximum meaning</span>
  </div>
</div>
`;
        return text;
    }

    getCheckboxElementHtml(field: IField): string {
        const id = `${field.id}-input-field`;
        const inputFieldText = `
            <input id="${id}" field-id="${field.id}" type="checkbox" class="auto-width" ${dataAttributesFalse(['editable'])}
        ${dataAttributesForInactiveElement()}
        oninput="${this.validateFnName}('${field.id}', this, '${field.id}-field-validation-errors')" />
            <label for="${id}" class="aza-text" ${dataAttributesFalse(['editable'])} data-azavista-translatable>` +
            `${this.utilsSvc.escapeHtml(field.label)} <span></span></label>
            `;
        return inputFieldText;
    }

    getDropdownElementHtml(field: IField, items: string[], isMultiSelect: boolean): string {
        const id = `${field.id}-input-field`;
        const multiple = isMultiSelect ? 'multiple' : '';
        const optionsHtmlItems: string[] = [];
        optionsHtmlItems.push('<option selected disabled hidden style="display: none" value=""></option>');
        const isFieldPreferredLanguage = field.name === 'preferred_language';
        if (!isFieldPreferredLanguage) {
            for (const item of items) {
                const sanitizedItem = this.utilsSvc.escapeHtml(item);
                const translatedLabel = this.getTranslationFromField(field, sanitizedItem);
                optionsHtmlItems.push(`<option value="${sanitizedItem}">${translatedLabel ?? sanitizedItem}</option>`);
            }
        } else {
            // Preferred language field needs special processing
            // We will put placeholders and supported languages (part of event settings)
            // will be filled when participant app renders the page
            optionsHtmlItems.push('<option>Sample language 1</option>');
            optionsHtmlItems.push('<option>Sample language 2</option>');
            optionsHtmlItems.push('<option>...</option>');
        }
        const optionsHtml = optionsHtmlItems.join('');
        const inputFieldText = `
<div ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
  <label class="aza-text" ${dataAttributesFalse(['editable'])} data-azavista-translatable>
        ${this.utilsSvc.escapeHtml(field.label)} <span></span></label>
</div>
<div ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
  <select id="${id}" field-id="${field.id}" ${multiple} style="width: 100%" ${dataAttributesFalse(['editable'])}
    ${dataAttributesForInactiveElement()}
    oninput="${this.validateFnName}('${field.id}', this, '${field.id}-field-validation-errors')">
    ${optionsHtml}
  </select>
</div>
`;
        return inputFieldText;
    }

    getTranslationFromField(field: IField, sanitizedItem: string): string | undefined {
        return field.attributeTranslations?.find(trans => trans.value === sanitizedItem)?.trans[this.translateSvc.currentLang];
    }

    getImageElementHtml(field: IField): string {
        const id = `${field.id}-input-field`;
        const imageFieldText = `
<div aza-image-field field-id="${field.id}" id="${id}" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
    <label for="${id}" class="aza-text" ${dataAttributesFalse(['editable'])} ${dataAttributesForInactiveElement()}
      data-azavista-translatable>${this.utilsSvc.escapeHtml(field.label)}</label>
    <div aza-image-field-img-element-container class="image-field-input-field-container" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
      <img aza-image-field-img-element class="image-field-input-field" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
    </div>
    <div aza-image-field-controls-container class="image-field-controls-container" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
      <span aza-image-field-control-select-file class="image-field-control image-field-control-select-file" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
          <path d="M0 0h24v24H0z" fill="none" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()} />
          <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()} />
        </svg>
      </span>
      <span aza-image-field-control-select-file class="image-field-control image-field-control-remove-file" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
            <path d="M0 0h24v24H0V0z" fill="none" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}/>
        </svg>
      </span>
    </div>
</div>
`;
        return imageFieldText;
    }

    getDocumentElementHtml(field: IField): string {
        const id = `${field.id}-input-field`;
        const documentFieldText = `
<div aza-document-field field-id="${field.id}" id="${id}" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
    <label for="${id}" class="aza-text" ${dataAttributesFalse(['editable'])}
      data-azavista-translatable>${this.utilsSvc.escapeHtml(field.label)}</label>
    <div aza-document-field-input-field-container class="document-field-input-field-container" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
        <input aza-document-field-input-field type="file" disabled class="document-field-input-field" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()} />
    </div>
    <div aza-document-field-upload-progress-container class="document-field-upload-progress-container" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
      <span aza-document-field-upload-progress-text class="document-field-upload-progress-text" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}></span>
    </div>
    <div aza-document-field-download-link-container class="document-field-download-link-container" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
        <a aza-document-field-download-link data-azavista-translatable class="document-field-download-link" ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>Download</a>
    </div>
</div>
`;
        return documentFieldText;
    }

    getReadOnlyElementHtml(field: IField): string {
        const id = `${field.id}-input-field`;
        const readonlyFieldText = `
<label for="${id}" class="aza-text" ${dataAttributesFalse(['editable'])}
   data-azavista-translatable>${this.utilsSvc.escapeHtml(field.label)} <span></span></label>
<span id="${id}" field-id="${field.id}"></span>
`;
        return readonlyFieldText;
    }

    createInputFieldBlock(field: IField): string {
        const fieldType = this.utilsSvc.getFieldTypeFromField(field);
        let editElementText = '';
        if (fieldType === 'date') {
            editElementText = this.getInputElementHtml(field, 'date');
        } else if (fieldType === 'integer' || fieldType === 'number') {
            editElementText = this.getInputElementHtml(field, 'number');
        } else if (fieldType === 'text' || fieldType === 'email' || fieldType === 'url' || fieldType === 'virtual-textarea') {
            editElementText = this.getInputElementHtml(field, 'text');
        } else if (fieldType === 'boolean') {
            editElementText = this.getCheckboxElementHtml(field);
        } else if (fieldType === 'select') {
            editElementText = this.getDropdownElementHtml(field, field.schema.enum as string[], false);
        } else if (fieldType === 'multi-select') {
            editElementText = this.getDropdownElementHtml(field, (field.schema.items as JSONSchema7).enum as string[], true);
        } else if (fieldType === 'unknown') {
            editElementText = this.getReadOnlyElementHtml(field);
        } else if (fieldType === 'datetime' || fieldType === 'travel-date') {
            editElementText = this.getInputElementHtml(field, 'datetime');
        } else if (fieldType === 'time') {
            editElementText = this.getInputElementHtml(field, 'time');
        } else if (fieldType === 'document') {
            editElementText = this.getDocumentElementHtml(field);
        } else if (fieldType === 'image') {
            editElementText = this.getImageElementHtml(field);
        } else if (fieldType === 'opinion-scale') {
            editElementText = this.getInputElementHtml(field, 'opinion-scale');
        } else if (fieldType === 'rating') {
            editElementText = this.getInputElementHtml(field, 'rating');
        } else {
            editElementText = this.getInputElementHtml(field, 'text');
        }

        const content = `
                <div aza-dependency class="aza-input-field" data-gjs-copyable="false" data-gjs-draggable=".aza-form-field-area"
                    data-gjs-droppable="false">
                    <div class="aza-field-content aza-field-type-${fieldType}"
                            ${dataAttributesFalse()} ${dataAttributesForInactiveElement()}>
                                ${editElementText}
                        <div id="${field.id}-field-validation-errors" class="aza-field-validation-error"></div>
                    </div>
                </div>
                <style>
                  .aza-input-field input, .aza-input-field textarea {
                    width: 100%;
                    box-sizing: border-box;
                  }
                  .aza-input-field input.auto-width {
                      width: auto;
                  }
                  .aza-field-validation-error {
                    position: absolute;
                    color: red;
                  }
                </style>
            `;
        return content;
    }
}
